import { BRAND } from './siteContent';

/** Public legal / policy pages for Success Bridge Academy (digital GED subscriptions). */

export const LEGAL_PAGES = {
  privacy: {
    eyebrow: 'Legal',
    title: 'Privacy Policy',
    updated: 'August 12, 2026',
    intro: [
      `${BRAND.name} ("${BRAND.name}," "we," "us," or "our") operates an AI-based GED preparation and educational platform through ${BRAND.website} (the "Website" or "Platform").`,
      'This Privacy Policy explains how we collect, use, store, protect, and disclose information when you use our Website, create an account, purchase a subscription, or use our educational services.',
      'By using our Website or services, you acknowledge that you have read and understood this Privacy Policy.',
    ],
    sections: [
      {
        heading: '1. Information We Collect',
        paragraphs: ['We may collect information that you provide directly to us, including:'],
        lists: [
          'Full name',
          'Email address',
          'Phone/mobile number',
          'Account username and password',
          'Billing and transaction information',
          'Subscription information',
          'Information provided when contacting customer support',
          'Educational information necessary to provide personalized learning services',
          'Quiz, practice test, and learning-progress information',
          'Information you submit to our AI-based learning tools',
          'Any other information you voluntarily provide to us',
        ],
        subSections: [
          {
            heading: 'Payment Information',
            paragraphs: [
              'Payments may be processed through third-party payment processors or payment gateways.',
              'When you make a card payment, payment information such as your card number, expiration date, CVV, or other sensitive payment credentials may be processed directly by the relevant payment provider.',
              `Unless expressly stated otherwise, ${BRAND.name} does not intentionally store complete credit or debit card numbers or CVV/security codes on its own servers.`,
              'Your payment information may be subject to the privacy policy and terms of the payment processor used to complete your transaction.',
            ],
          },
        ],
      },
      {
        heading: '2. Information Collected Automatically',
        paragraphs: [
          'When you access our Website, certain technical information may be collected automatically, including:',
        ],
        lists: [
          'IP address',
          'Browser type',
          'Device type',
          'Operating system',
          'Website pages visited',
          'Date and time of access',
          'Referring website',
          'General usage and interaction information',
          'Cookies and similar technologies',
        ],
        afterLists: [
          'We may use this information to operate, secure, analyze, and improve our Website and services.',
        ],
      },
      {
        heading: '3. How We Use Your Information',
        paragraphs: ['We may use your information to:'],
        lists: [
          'Create and manage your account',
          'Provide access to purchased services',
          'Process and confirm payments',
          'Activate subscriptions',
          'Provide customer support',
          'Provide AI-based educational services',
          'Personalize your learning experience',
          'Track educational progress',
          'Provide quizzes, practice exams, and learning recommendations',
          'Communicate with you about your account or subscription',
          'Send important service-related notifications',
          'Detect and prevent fraud, abuse, or unauthorized activity',
          'Maintain and improve our Website and services',
          'Comply with applicable legal and regulatory requirements',
          'Resolve disputes and enforce our Terms & Conditions',
        ],
      },
      {
        heading: '4. AI-Based Learning Services',
        paragraphs: [
          `${BRAND.name} provides AI-powered educational features, including an AI tutor, personalized study recommendations, quizzes, practice exams, and progress monitoring.`,
          'Information submitted through these features may be processed to provide and improve your educational experience.',
          'Users should avoid submitting unnecessary sensitive personal information through AI tools, quizzes, messages, or other areas of the Platform.',
        ],
      },
      {
        heading: '5. Cookies',
        paragraphs: ['Our Website may use cookies and similar technologies to:'],
        lists: [
          'Keep users logged in',
          'Remember preferences',
          'Maintain website functionality',
          'Understand website usage',
          'Improve website performance',
          'Help protect accounts and transactions',
        ],
        afterLists: [
          'You may be able to control cookies through your browser settings. Disabling certain cookies may affect some Website functionality.',
        ],
      },
      {
        heading: '6. Sharing of Information',
        paragraphs: [
          'We may share information with trusted third-party service providers when necessary to operate our business and provide our services.',
          'These may include:',
        ],
        lists: [
          'Payment processors and payment gateways',
          'Website hosting providers',
          'Cloud service providers',
          'Analytics and security providers',
          'Email and communication providers',
          'Technical service providers',
          'Professional advisers where reasonably necessary',
        ],
        afterLists: [
          'We do not sell your personal information as a commercial product.',
          'We may also disclose information where required by law, legal process, court order, governmental authority, or where reasonably necessary to protect our rights, users, property, or security.',
        ],
      },
      {
        heading: '7. Payment Processing',
        paragraphs: [
          `When you purchase a ${BRAND.name} subscription, your transaction may be processed through a third-party payment gateway.`,
          'The payment gateway may collect and process payment information according to its own privacy policy, security requirements, and terms.',
          `${BRAND.name} may receive limited transaction information, such as:`,
        ],
        lists: [
          'Transaction/reference number',
          'Payment status',
          'Amount paid',
          'Currency',
          'Date of transaction',
          'Purchased service',
          'Customer/account information associated with the transaction',
        ],
      },
      {
        heading: '8. Data Security',
        paragraphs: [
          'We take reasonable administrative, technical, and organizational measures to protect personal information against unauthorized access, misuse, alteration, disclosure, or destruction.',
          'However, no internet transmission or electronic storage system can be guaranteed to be completely secure.',
          'You are responsible for keeping your account credentials confidential and should notify us if you believe your account has been accessed without authorization.',
        ],
      },
      {
        heading: '9. Data Retention',
        paragraphs: ['We retain personal information for as long as reasonably necessary to:'],
        lists: [
          'Provide our services',
          'Maintain your account',
          'Complete transactions',
          'Maintain business and transaction records',
          'Resolve disputes',
          'Prevent fraud or abuse',
          'Comply with legal and regulatory obligations',
        ],
        afterLists: [
          'When information is no longer reasonably required, we may delete, anonymize, or securely dispose of it, subject to applicable legal requirements.',
        ],
      },
      {
        heading: '10. Third-Party Websites and Services',
        paragraphs: [
          'Our Website may contain links to third-party websites, services, or platforms.',
          'We are not responsible for the privacy practices, security, content, or policies of third-party websites.',
          'You should review the privacy policy of any third-party service before providing personal information.',
        ],
      },
      {
        heading: "11. Children's Privacy",
        paragraphs: [
          'Our services are intended for users who are legally permitted to use the services in their jurisdiction.',
          'If a parent or legal guardian believes that a child has provided personal information to us without appropriate authorization, they may contact us using the information below.',
        ],
      },
      {
        heading: '12. Your Rights',
        paragraphs: [
          'Depending on applicable law, you may have rights relating to your personal information, including requesting access to, correction of, or deletion of certain information.',
          'Requests may be submitted using our contact information below.',
          'We may need to verify your identity before processing certain requests.',
        ],
      },
      {
        heading: '13. Changes to This Privacy Policy',
        paragraphs: [
          'We may update this Privacy Policy from time to time.',
          'When changes are made, we may update the "Last Updated" date at the top of this policy.',
          'Your continued use of the Website after an updated Privacy Policy becomes effective constitutes acceptance of the updated policy to the extent permitted by applicable law.',
        ],
      },
    ],
    contact: {
      heading: '14. Contact Us',
      lead: 'If you have questions about this Privacy Policy or how your information is handled, contact us:',
      name: BRAND.name,
      website: BRAND.website,
      websiteUrl: BRAND.websiteUrl,
      email: BRAND.email,
      phone: BRAND.phoneDisplay,
      address: BRAND.address,
    },
  },

  refund: {
    eyebrow: 'Legal',
    title: 'Refund & Cancellation Policy',
    updated: 'August 16, 2026',
    sections: [
      {
        heading: 'Subscriptions are digital services',
        paragraphs: [
          `${BRAND.name} sells time-limited access to our online GED learning portal (lessons, AI tutor, quizzes, practice exams, and related features). There is no physical product shipment.`,
        ],
      },
      {
        heading: 'Cooling-off / early cancellation',
        paragraphs: [
          'If you have not meaningfully used the paid portal (for example you have not started course lessons, AI tutoring, or practice exams) and you request a cancellation within 48 hours of a successful subscription payment, you may request a full refund of that subscription payment.',
          'Once you begin using paid portal features, the subscription fee is generally non-refundable because digital access has been delivered.',
        ],
      },
      {
        heading: 'How to cancel or request a refund',
        paragraphs: [
          `Email ${BRAND.email} or use the Contact page with your full name, account email, payment reference (if available), and reason for the request. Our team will review and confirm the outcome.`,
        ],
      },
      {
        heading: 'Approved refunds',
        paragraphs: [
          'Approved refunds are processed using the original payment method or another method we arrange with you. Processing typically completes within 7–14 business days after approval, depending on your bank or payment provider.',
        ],
      },
      {
        heading: 'Extensions & optional fees',
        paragraphs: [
          'Day extensions and optional fees (such as exam or diploma-related fees when offered) follow the same review process. Refunds are not available after the add-on access or service has been applied to your account, except where required by law or where we determine a billing error occurred.',
        ],
      },
      {
        heading: 'Chargebacks',
        paragraphs: [
          'Please contact us before filing a chargeback so we can help resolve the issue. Unfounded chargebacks may result in suspension of portal access while the dispute is investigated.',
        ],
      },
    ],
  },

  delivery: {
    eyebrow: 'Legal',
    title: 'Service Delivery Policy',
    updated: 'August 16, 2026',
    sections: [
      {
        heading: 'What we deliver',
        paragraphs: [
          `${BRAND.name} delivers digital GED preparation services: cloud access to the student portal, curriculum for Mathematical Reasoning, Science, Reasoning Through Language Arts, and Social Studies, AI tutoring, quizzes, practice exams, progress tracking, and advisor-backed enrollment support.`,
        ],
      },
      {
        heading: 'When access begins',
        paragraphs: [
          'After successful payment and account activation, subscription access is typically available immediately in your dashboard. In rare cases (payment review, device approval, or technical delay), activation may take up to one business day. We will notify you if extra verification is required.',
        ],
      },
      {
        heading: 'Duration',
        paragraphs: [
          'Access lasts for the duration of your selected study plan (for example 1, 2, or 3 months) from activation, unless extended by an approved day extension or adjusted by our team for a documented billing correction.',
        ],
      },
      {
        heading: 'How you receive the service',
        paragraphs: [
          'All services are delivered online through our website and student portal. You need a compatible device, a modern browser, and a stable internet connection. We do not mail physical study materials as part of standard plans.',
        ],
      },
      {
        heading: 'Support hours',
        paragraphs: [
          'Advisor and support responses are generally available Monday–Saturday, 8:00–18:00 (local academy support hours). AI tutoring inside the portal is available 24/7 for enrolled students with an active subscription.',
        ],
      },
      {
        heading: 'Service interruptions',
        paragraphs: [
          'We aim for reliable uptime. Planned maintenance or unexpected outages may occasionally limit access. Where a prolonged outage materially prevents use of paid features, contact us and we will work with you on a fair remedy (such as access extension).',
        ],
      },
    ],
  },

  terms: {
    eyebrow: 'Legal',
    title: 'Terms & Conditions',
    updated: 'August 16, 2026',
    sections: [
      {
        heading: 'Agreement',
        paragraphs: [
          `By creating an account, enrolling, or paying for services at ${BRAND.name}, you agree to these Terms & Conditions and our Privacy Policy.`,
        ],
      },
      {
        heading: 'Eligibility & accounts',
        paragraphs: [
          'You must provide accurate enrollment information and keep your login credentials confidential. You are responsible for activity under your account. Sharing paid portal access with others is not allowed.',
        ],
      },
      {
        heading: 'Plans, pricing & payment',
        paragraphs: [
          'Study plan prices, discounts, and features are shown on the Pricing and Checkout pages and may change until payment is completed. Completing checkout creates a payment record and activates access according to the plan you purchased.',
          'Our online payment gate may operate in demo or live mode depending on academy configuration. You agree to pay all amounts due for selected plans and add-ons.',
        ],
      },
      {
        heading: 'Acceptable use',
        paragraphs: [
          'You may use the portal only for lawful personal learning. You must not attempt to disrupt the service, reverse engineer proprietary systems, abuse the AI tutor, cheat on assessments in ways that harm other learners, or upload harmful content.',
        ],
      },
      {
        heading: 'Educational disclaimer',
        paragraphs: [
          'We provide preparation tools and practice aligned to GED-style learning. We do not guarantee exam results. Official GED testing is administered by authorized testing bodies; you are responsible for meeting their requirements and scheduling.',
        ],
      },
      {
        heading: 'Intellectual property',
        paragraphs: [
          'Course content, branding, software, and materials on our site remain the property of Success Bridge Academy or its licensors. You receive a limited, non-transferable license to use paid content during your active subscription.',
        ],
      },
      {
        heading: 'Suspension & termination',
        paragraphs: [
          'We may suspend or terminate access for unpaid balances, fraud, policy violations, or security risks. You may stop using the service at any time; refund eligibility is governed by our Refund & Cancellation Policy.',
        ],
      },
      {
        heading: 'Limitation of liability',
        paragraphs: [
          'To the fullest extent permitted by law, we are not liable for indirect or consequential damages arising from use of the service. Our total liability for a claim related to a purchase is limited to the amount you paid for that purchase.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          `Questions about these terms: ${BRAND.email} or the Contact page on our website.`,
        ],
      },
    ],
  },
};
